import React, { memo, useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import type { Variant } from '@acre/design-system'
import { withDisabled } from '@acre/design-system'
import { testHandle } from '@acre/utils'

import { convertToLutherAddress, formatLutherAddress } from './AddressLookup.helpers'
import AddressManual from './AddressManual'
import Lookup from './Lookup'

type Props = {
  address: LutherAddress
  onChange: (address: LutherAddress) => any
  propertyFieldsUpdated?: (id: number, fields: PropertyFieldUpdates) => any
  label?: string
  showLookup?: boolean
  variant?: Variant
  errors?: { [key: string]: { message: string } }
  fieldPrefix?: string
  disabled?: boolean
  isMissing?: boolean
  secondaryText?: ReactNode
}

const AddressLookup = withDisabled(
  ({
    address,
    errors,
    label,
    onChange,
    propertyFieldsUpdated,
    fieldPrefix = '',
    showLookup = true,
    variant = 'default',
    disabled = false,
    isMissing,
    secondaryText,
  }: Props) => {
    const [isAddressLookupVisible, setIsAddressLookupVisible] = useState(showLookup)

    useEffect(() => {
      if (isAddressLookupVisible !== showLookup) {
        setIsAddressLookupVisible(showLookup)
      }
    }, [showLookup])

    const handleSetAddress = useCallback((newAddress: AddressAndFieldUpdates) => {
      onChange(convertToLutherAddress(newAddress as Address))
      if (propertyFieldsUpdated && newAddress.field_updates) {
        propertyFieldsUpdated(newAddress.property_id || 0, newAddress.field_updates)
      }
      setIsAddressLookupVisible(false)
    }, [])

    const handleLookupClick = useCallback(() => setIsAddressLookupVisible(false), [])
    const handleAddressManualClick = useCallback(() => setIsAddressLookupVisible(true), [])

    return (
      <div data-testid={testHandle('AddressLookup')}>
        {isAddressLookupVisible ? (
          <Lookup
            label={label}
            secondaryText={secondaryText}
            onClick={handleLookupClick}
            onGetAddress={(newAddress) => handleSetAddress(newAddress)}
            variant={variant}
            disabled={disabled}
            isMissing={isMissing}
          />
        ) : (
          <AddressManual
            address={formatLutherAddress(address)}
            onChange={handleSetAddress}
            onClick={handleAddressManualClick}
            variant={variant}
            errors={errors}
            fieldPrefix={fieldPrefix}
            disabled={disabled}
            address1IsMissing={isMissing && !address?.address1}
            posttownIsMissing={isMissing && !address?.posttown}
            postcodeIsMissing={isMissing && !address?.postcode}
          />
        )}
      </div>
    )
  },
)
export default memo(AddressLookup)
